import { useCallback, useEffect, useState } from 'react';

import { useQuery } from 'react-apollo';
import uuid from 'uuid';

import {
	AnalyticsEventSource,
	getApolloClient,
	useAnalytics,
	usePrincipalUser,
	useSite,
} from '@atlassian/ptc-common';

import ContainersQuery from './ContainersQuery.graphql';
import {
	type CollaborationGraphContainerFormat,
	toContainersViewFormat,
} from './transformToContainersViewFormat';
import { type ContainersQueryData, type ContainersQueryProps } from './types';

interface QueryData {
	Containers: {
		collaborationGraphEntities: CollaborationGraphContainerFormat[];
	};
}

const getCurrentTime = () => (window && window.performance ? window.performance.now() : Date.now());

function useContainersQuery(props: ContainersQueryProps): ContainersQueryData {
	const [{ cloudId }] = useSite();
	const [{ principalUserId }] = usePrincipalUser();
	const analytics = useAnalytics();

	const [sessionId] = useState(uuid());
	const [startTime, setStartTime] = useState(0);

	useEffect(() => {
		setStartTime(getCurrentTime());
	}, []);

	const userId = props.userId || principalUserId;

	const triggerAnalyticsEvent = useCallback(
		(elapsedTimeMs: number, data: QueryData) => {
			const containers = (data.Containers && data.Containers.collaborationGraphEntities) || [];
			const ids = containers.map(({ id }) => id);
			analytics.pushOperationalEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'collaboration-graph',
				attributes: {
					resultLength: ids.length,
					sessionId,
					resultIds: ids,
					endpoint: 'userToContainer',
					userId,
					elapsedTimeMs,
				},
				action: 'requested',
			});
		},
		[userId, analytics, sessionId],
	);

	const handleOnCompleted = useCallback(
		(data: QueryData) => {
			const end = getCurrentTime();
			const diff = end - startTime;
			triggerAnalyticsEvent(diff, data || {});
		},
		[startTime, triggerAnalyticsEvent],
	);

	const { loading, error, data } = useQuery(ContainersQuery, {
		client: getApolloClient(),
		variables: {
			siteId: cloudId,
			userId,
			principalId: principalUserId,
			maxNumberOfResults: props.maxNumberOfCards,
			sessionId,
		},
		errorPolicy: 'all',
		skip: !principalUserId,
		onCompleted: handleOnCompleted,
	});

	return {
		data: data ? toContainersViewFormat(data.Containers?.collaborationGraphEntities) : [],
		loading,
		hasError: !!error,
		error: error,
	};
}

export default useContainersQuery;
